import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { apiFetch } from '@/lib/api';
import type {
  AppAdmin,
  AppAdminsResponse,
  AppUsageResponse,
  BusinessLineResponse,
  BusinessLinesResponse,
  DepartmentsResponse,
  Doc,
  EmployeesUploadResult,
  Kpis,
  LastEmployeesImport,
  ManagerView,
  Me,
  RiskResponse,
  TrendResponse,
  UserDetail,
  UserSearchResponse,
  UsersQuery,
  UsersResponse,
} from '@/lib/types';

export function useMe() {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<Me>(instance, '/api/me'),
  });
}

export function useKpis() {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['kpis'],
    queryFn: () => apiFetch<Kpis>(instance, '/api/kpis'),
  });
}

export function useUsageByApp(businessLine: string | null) {
  const { instance } = useMsal();
  const qs = businessLine
    ? `?business_line=${encodeURIComponent(businessLine)}`
    : '';
  return useQuery({
    queryKey: ['usage', 'by-app', businessLine],
    queryFn: () => apiFetch<AppUsageResponse>(instance, `/api/usage/by-app${qs}`),
    placeholderData: (prev) => prev,
  });
}

export function useUsageByBusinessLine() {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['usage', 'by-business-line'],
    queryFn: () =>
      apiFetch<BusinessLineResponse>(instance, '/api/usage/by-business-line'),
  });
}

export function useAtRisk(businessLine: string | null, limit = 10) {
  const { instance } = useMsal();
  const params = new URLSearchParams({ limit: String(limit) });
  if (businessLine) params.set('business_line', businessLine);
  return useQuery({
    queryKey: ['usage', 'at-risk', businessLine, limit],
    queryFn: () =>
      apiFetch<RiskResponse>(instance, `/api/usage/at-risk?${params.toString()}`),
    placeholderData: (prev) => prev,
  });
}

export function useBusinessLinesAdmin(enabled: boolean) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['admin', 'business-lines'],
    queryFn: () =>
      apiFetch<BusinessLinesResponse>(instance, '/api/admin/business-lines'),
    enabled,
  });
}

export function useUserSearch(q: string) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['admin', 'search', q],
    queryFn: () =>
      apiFetch<UserSearchResponse>(
        instance,
        `/api/admin/users-search?q=${encodeURIComponent(q)}`,
      ),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useCreateAssignment() {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { user_oid: string; business_line: string }) =>
      apiFetch<ManagerView>(instance, '/api/admin/assignments', {
        method: 'POST',
        body: JSON.stringify(p),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'business-lines'] });
    },
  });
}

export function useDoc(slug: string, enabled = true) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['doc', slug],
    queryFn: () => apiFetch<Doc>(instance, `/api/docs/${slug}`),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateDoc(slug: string) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { title: string; content_md: string }) =>
      apiFetch<Doc>(instance, `/api/docs/${slug}`, {
        method: 'PUT',
        body: JSON.stringify(p),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['doc', slug] });
    },
  });
}

export function useAppAdmins(enabled: boolean) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['admin', 'app-admins'],
    queryFn: () => apiFetch<AppAdminsResponse>(instance, '/api/admin/app-admins'),
    enabled,
  });
}

export function useGrantAppAdmin() {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (user_oid: string) =>
      apiFetch<AppAdmin>(instance, '/api/admin/app-admins', {
        method: 'POST',
        body: JSON.stringify({ user_oid }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'app-admins'] });
    },
  });
}

export function useRevokeAppAdmin() {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (user_oid: string) =>
      apiFetch<void>(
        instance,
        `/api/admin/app-admins/${encodeURIComponent(user_oid)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'app-admins'] });
    },
  });
}

export function useLastEmployeesImport(enabled: boolean) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['admin', 'last-employees-import'],
    queryFn: () =>
      apiFetch<LastEmployeesImport>(instance, '/api/admin/employees/last-import'),
    enabled,
  });
}

export function useUploadEmployees(dryRun: boolean) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiFetch<EmployeesUploadResult>(
        instance,
        `/api/admin/employees/upload?dry_run=${dryRun}`,
        { method: 'POST', body: fd },
      );
    },
    onSuccess: (result) => {
      if (!result.dry_run) {
        void qc.invalidateQueries({ queryKey: ['admin', 'business-lines'] });
        void qc.invalidateQueries({ queryKey: ['admin', 'last-employees-import'] });
      }
    },
  });
}

export function useDeleteAssignment() {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { user_oid: string; business_line: string }) =>
      apiFetch<void>(
        instance,
        `/api/admin/assignments/${encodeURIComponent(p.user_oid)}/${encodeURIComponent(p.business_line)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'business-lines'] });
    },
  });
}

export function useUsageTrend(businessLine: string | null, days = 30) {
  const { instance } = useMsal();
  const params = new URLSearchParams({ days: String(days) });
  if (businessLine) params.set('business_line', businessLine);
  return useQuery({
    queryKey: ['usage', 'trend', businessLine, days],
    queryFn: () =>
      apiFetch<TrendResponse>(instance, `/api/usage/trend?${params.toString()}`),
    placeholderData: (prev) => prev,
  });
}

export function useDepartments(businessLine: string | null) {
  const { instance } = useMsal();
  const qs = businessLine ? `?business_line=${encodeURIComponent(businessLine)}` : '';
  return useQuery({
    queryKey: ['departments', businessLine],
    queryFn: () => apiFetch<DepartmentsResponse>(instance, `/api/users/departments${qs}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserDetail(upn: string | null) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['user-detail', upn],
    queryFn: () =>
      apiFetch<UserDetail>(instance, `/api/users/${encodeURIComponent(upn!)}`),
    enabled: upn !== null,
  });
}

export function useUsers(q: UsersQuery) {
  const { instance } = useMsal();
  const params = new URLSearchParams({
    page: String(q.page),
    page_size: String(q.pageSize),
  });
  if (q.businessLine) params.set('business_line', q.businessLine);
  if (q.onlyInactive) params.set('only_inactive', 'true');
  if (q.activeInApp) params.set('active_in_app', q.activeInApp);
  if (q.q && q.q.trim()) params.set('q', q.q.trim());
  if (q.department) params.set('department', q.department);
  if (q.hasLicense === true) params.set('has_license', 'true');
  else if (q.hasLicense === false) params.set('has_license', 'false');
  if (q.sortBy) params.set('sort_by', q.sortBy);
  if (q.sortDir) params.set('sort_dir', q.sortDir);
  if (q.periodDays) params.set('period_days', String(q.periodDays));
  return useQuery({
    queryKey: ['users', q],
    queryFn: () => apiFetch<UsersResponse>(instance, `/api/users?${params.toString()}`),
    placeholderData: (prev) => prev,
  });
}
